/**
 * OperatorRoute — F-007 Phase 4 (IMPL-P4-002).
 *
 * Route guard dedicato alle pagine `/operatore` e `/operatore/:id`.
 * Replica la semantica di `RequireAuth` ma con redirect mirati al flusso operatore:
 *   - `loading`        → placeholder minimale (evita flash di redirect su refresh)
 *   - `unauthenticated`→ redirect a `/accedi` (login Better Auth)
 *   - role non operativo (CLIENT) → redirect a `/`
 *
 * Ruoli ammessi: `OPERATOR` e `ADMIN` (l'admin eredita le capability operative,
 * cfr. INTRODUCTION.md §"Architectural Decisions" → `requireOperator()`).
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthSession } from '@/features/auth/hooks/useAuthSession';

interface OperatorRouteProps {
  children: ReactNode;
}

export function OperatorRoute({ children }: OperatorRouteProps) {
  const { user, status } = useAuthSession();

  if (status === 'loading') {
    return (
      <main
        role="status"
        aria-live="polite"
        className="min-h-screen bg-background text-foreground flex items-center justify-center p-6"
      >
        <p className="text-sm text-muted-foreground">Caricamento in corso…</p>
      </main>
    );
  }

  if (status === 'unauthenticated' || !user) {
    return <Navigate to="/accedi" replace />;
  }

  if (user.role !== 'OPERATOR' && user.role !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
